import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CalendarDays, Clock, User, Mail, Phone, MessageSquare, CheckCircle, Loader2, ArrowRight, ArrowLeft, Briefcase } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import Navbar from '../components/Navbar';
import { Footer } from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const services = {
  fr: [
    { id: 'applications', label: 'Applications Mobiles & Web', desc: 'Android, iOS, Web Apps, SaaS' },
    { id: 'websites', label: 'Sites Web', desc: 'Vitrine, Plateforme, Landing Page' },
    { id: 'automation', label: 'Automatisation & IA', desc: 'Workflows, Chatbots, Intégrations' },
    { id: 'formation', label: 'Formation', desc: 'Anglais, informatique, compétences numériques' },
    { id: 'other', label: 'Autre projet', desc: 'Consultation, conseil, autre' },
  ],
  en: [
    { id: 'applications', label: 'Mobile & Web Apps', desc: 'Android, iOS, Web Apps, SaaS' },
    { id: 'websites', label: 'Websites', desc: 'Showcase, Platform, Landing Page' },
    { id: 'automation', label: 'Automation & AI', desc: 'Workflows, Chatbots, Integrations' },
    { id: 'formation', label: 'Training', desc: 'English, IT, digital skills' },
    { id: 'other', label: 'Other project', desc: 'Consultation, advice, other' },
  ]
};

const timeSlots = [
  '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
  '13:00', '13:30', '14:00', '14:30', '15:00', '15:30', '16:00', '16:30'
];

const t = {
  fr: {
    badge: 'RENDEZ-VOUS',
    title: 'Planifiez votre consultation',
    subtitle: 'Choisissez un créneau pour discuter de votre projet ou de nos formations avec notre équipe.',
    step1: 'Service',
    step2: 'Date & Heure',
    step3: 'Vos coordonnées',
    selectService: 'Quel type de projet ?',
    selectDate: 'Choisissez une date',
    selectTime: 'Choisissez un créneau',
    name: 'Nom complet',
    email: 'Adresse email',
    phone: 'Numéro de téléphone',
    message: 'Décrivez brièvement votre projet (optionnel)',
    next: 'Suivant',
    prev: 'Retour',
    submit: 'Confirmer le rendez-vous',
    submitting: 'Envoi en cours...',
    successTitle: 'Rendez-vous demandé !',
    successMsg: 'Nous vous contacterons pour confirmer votre créneau.',
    newAppointment: 'Nouveau rendez-vous',
    morning: 'Matin',
    afternoon: 'Après-midi',
    days: ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'],
    months: ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'],
  },
  en: {
    badge: 'APPOINTMENT',
    title: 'Schedule your consultation',
    subtitle: 'Choose a time slot to discuss your project or our training programs with our team.',
    step1: 'Service',
    step2: 'Date & Time',
    step3: 'Your details',
    selectService: 'What type of project?',
    selectDate: 'Choose a date',
    selectTime: 'Choose a time slot',
    name: 'Full name',
    email: 'Email address',
    phone: 'Phone number',
    message: 'Briefly describe your project (optional)',
    next: 'Next',
    prev: 'Back',
    submit: 'Confirm appointment',
    submitting: 'Sending...',
    successTitle: 'Appointment requested!',
    successMsg: 'We will contact you to confirm your time slot.',
    newAppointment: 'New appointment',
    morning: 'Morning',
    afternoon: 'Afternoon',
    days: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
  }
};

function MiniCalendar({ selected, onSelect, lang }) {
  const labels = t[lang];
  const today = new Date();
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [viewYear, setViewYear] = useState(today.getFullYear());

  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const firstDay = new Date(viewYear, viewMonth, 1).getDay();
  const days = [];
  for (let i = 0; i < firstDay; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) days.push(d);

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(viewYear - 1); }
    else setViewMonth(viewMonth - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(viewYear + 1); }
    else setViewMonth(viewMonth + 1);
  };

  const isDisabled = (day) => {
    if (!day) return true;
    const date = new Date(viewYear, viewMonth, day);
    if (date < new Date(today.getFullYear(), today.getMonth(), today.getDate())) return true;
    if (date.getDay() === 0) return true; // Sunday
    return false;
  };

  const isSelected = (day) => {
    if (!day || !selected) return false;
    return selected === `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  return (
    <div className="bg-[#0f2132] rounded-2xl p-5 border border-white/5">
      <div className="flex items-center justify-between mb-4">
        <button onClick={prevMonth} className="p-2 rounded-lg hover:bg-white/10 text-white/60 hover:text-white transition-colors">
          <ArrowLeft size={16} />
        </button>
        <span className="text-white font-semibold">{labels.months[viewMonth]} {viewYear}</span>
        <button onClick={nextMonth} className="p-2 rounded-lg hover:bg-white/10 text-white/60 hover:text-white transition-colors">
          <ArrowRight size={16} />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 mb-2">
        {labels.days.map(d => <div key={d} className="text-center text-xs text-white/40 font-medium py-1">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {days.map((day, i) => (
          <button
            key={i}
            disabled={isDisabled(day)}
            onClick={() => day && !isDisabled(day) && onSelect(`${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`)}
            className={`h-9 w-full rounded-lg text-sm transition-all ${
              !day ? '' :
              isDisabled(day) ? 'text-white/15 cursor-not-allowed' :
              isSelected(day) ? 'bg-[#00FFFF] text-[#030444] font-bold' :
              'text-white/80 hover:bg-[#4A90D9]/30 hover:text-white cursor-pointer'
            }`}
            data-testid={day ? `cal-day-${day}` : undefined}
          >
            {day}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function AppointmentPage() {
  const { language } = useLanguage();
  const labels = t[language];
  const currentServices = services[language];

  const [step, setStep] = useState(1);
  const [selectedService, setSelectedService] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const canNext = () => {
    if (step === 1) return !!selectedService;
    if (step === 2) return !!selectedDate && !!selectedTime;
    if (step === 3) return form.name.trim() && form.email.trim() && form.phone.trim();
    return false;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await fetch(`${API_URL}/api/appointments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name, email: form.email, phone: form.phone,
          service: selectedService, date: selectedDate, time: selectedTime,
          message: form.message
        })
      });
      setSuccess(true);
    } catch (err) { console.error(err); }
    finally { setSubmitting(false); }
  };

  const reset = () => {
    setStep(1); setSelectedService(''); setSelectedDate(''); setSelectedTime('');
    setForm({ name: '', email: '', phone: '', message: '' }); setSuccess(false);
  };

  const morningSlots = timeSlots.filter(s => parseInt(s) < 12);
  const afternoonSlots = timeSlots.filter(s => parseInt(s) >= 12);

  const stepIndicator = (
    <div className="flex items-center justify-center gap-2 mb-8" data-testid="step-indicator">
      {[1, 2, 3].map(s => (
        <div key={s} className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
            s === step ? 'bg-[#00FFFF] text-[#030444]' : s < step ? 'bg-[#4A90D9] text-white' : 'bg-white/10 text-white/30'
          }`}>{s < step ? '✓' : s}</div>
          {s < 3 && <div className={`w-12 h-0.5 ${s < step ? 'bg-[#4A90D9]' : 'bg-white/10'}`} />}
        </div>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0B1521]" data-testid="appointment-page">
      <Navbar />

      <section className="pt-32 pb-16 px-4">
        <div className="max-w-2xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-10">
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#00FFFF]/10 text-[#00FFFF] text-xs font-bold tracking-widest mb-4">{labels.badge}</span>
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3">{labels.title}</h1>
            <p className="text-white/50 text-sm sm:text-base">{labels.subtitle}</p>
          </motion.div>

          {!success && stepIndicator}

          <AnimatePresence mode="wait">
            {success ? (
              <motion.div key="success" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="bg-[#0f2132] rounded-2xl p-8 text-center border border-[#00FFFF]/20" data-testid="appointment-success">
                <div className="w-16 h-16 rounded-full bg-[#00FFFF]/20 flex items-center justify-center mx-auto mb-5">
                  <CheckCircle size={32} className="text-[#00FFFF]" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">{labels.successTitle}</h2>
                <p className="text-white/50 mb-6">{labels.successMsg}</p>
                <div className="bg-[#030444]/50 rounded-xl p-4 mb-6 text-left space-y-2">
                  <p className="text-white/70 text-sm"><span className="text-white/40">{labels.step1}:</span> {currentServices.find(s => s.id === selectedService)?.label}</p>
                  <p className="text-white/70 text-sm"><span className="text-white/40">{labels.step2}:</span> {selectedDate} — {selectedTime}</p>
                  <p className="text-white/70 text-sm"><span className="text-white/40">{labels.name}:</span> {form.name}</p>
                </div>
                <button onClick={reset} className="px-6 py-3 bg-[#00FFFF] text-[#030444] font-bold rounded-xl hover:bg-[#00FFFF]/80 transition-colors" data-testid="new-appointment-btn">{labels.newAppointment}</button>
              </motion.div>
            ) : (
              <motion.div key={`step-${step}`} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}>
                {/* Step 1: Service Selection */}
                {step === 1 && (
                  <div className="space-y-3" data-testid="step-service">
                    <h3 className="text-white/60 text-sm font-medium mb-4">{labels.selectService}</h3>
                    {currentServices.map(s => (
                      <button key={s.id} onClick={() => setSelectedService(s.id)}
                        className={`w-full text-left p-4 rounded-xl border transition-all ${
                          selectedService === s.id ? 'bg-[#00FFFF]/10 border-[#00FFFF]/40 text-white' : 'bg-[#0f2132] border-white/5 text-white/70 hover:border-white/20'
                        }`} data-testid={`service-${s.id}`}>
                        <div className="flex items-center gap-3">
                          <Briefcase size={20} className={selectedService === s.id ? 'text-[#00FFFF]' : 'text-white/30'} />
                          <div>
                            <p className="font-semibold text-sm">{s.label}</p>
                            <p className="text-xs text-white/40 mt-0.5">{s.desc}</p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {/* Step 2: Date & Time */}
                {step === 2 && (
                  <div data-testid="step-datetime">
                    <h3 className="text-white/60 text-sm font-medium mb-4">{labels.selectDate}</h3>
                    <MiniCalendar selected={selectedDate} onSelect={setSelectedDate} lang={language} />

                    {selectedDate && (
                      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-6">
                        <h3 className="text-white/60 text-sm font-medium mb-3">{labels.selectTime}</h3>
                        <div className="space-y-4">
                          <div>
                            <p className="text-xs text-white/30 mb-2">{labels.morning}</p>
                            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                              {morningSlots.map(slot => (
                                <button key={slot} onClick={() => setSelectedTime(slot)}
                                  className={`py-2.5 rounded-lg text-sm font-medium transition-all ${
                                    selectedTime === slot ? 'bg-[#00FFFF] text-[#030444] font-bold' : 'bg-[#0f2132] text-white/60 hover:bg-[#4A90D9]/20 hover:text-white border border-white/5'
                                  }`} data-testid={`time-${slot}`}>{slot}</button>
                              ))}
                            </div>
                          </div>
                          <div>
                            <p className="text-xs text-white/30 mb-2">{labels.afternoon}</p>
                            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                              {afternoonSlots.map(slot => (
                                <button key={slot} onClick={() => setSelectedTime(slot)}
                                  className={`py-2.5 rounded-lg text-sm font-medium transition-all ${
                                    selectedTime === slot ? 'bg-[#00FFFF] text-[#030444] font-bold' : 'bg-[#0f2132] text-white/60 hover:bg-[#4A90D9]/20 hover:text-white border border-white/5'
                                  }`} data-testid={`time-${slot}`}>{slot}</button>
                              ))}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </div>
                )}

                {/* Step 3: Contact Info */}
                {step === 3 && (
                  <div className="space-y-4" data-testid="step-contact">
                    <h3 className="text-white/60 text-sm font-medium mb-4">{labels.step3}</h3>
                    <div className="relative">
                      <User size={16} className="absolute left-3 top-3.5 text-white/30" />
                      <input type="text" placeholder={labels.name} value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                        className="w-full bg-[#0f2132] border border-white/10 rounded-xl pl-10 pr-4 py-3 text-white text-sm placeholder-white/30 focus:border-[#00FFFF]/40 focus:outline-none transition-colors" data-testid="input-name" />
                    </div>
                    <div className="relative">
                      <Mail size={16} className="absolute left-3 top-3.5 text-white/30" />
                      <input type="email" placeholder={labels.email} value={form.email} onChange={e => setForm({...form, email: e.target.value})}
                        className="w-full bg-[#0f2132] border border-white/10 rounded-xl pl-10 pr-4 py-3 text-white text-sm placeholder-white/30 focus:border-[#00FFFF]/40 focus:outline-none transition-colors" data-testid="input-email" />
                    </div>
                    <div className="relative">
                      <Phone size={16} className="absolute left-3 top-3.5 text-white/30" />
                      <input type="tel" placeholder={labels.phone} value={form.phone} onChange={e => setForm({...form, phone: e.target.value})}
                        className="w-full bg-[#0f2132] border border-white/10 rounded-xl pl-10 pr-4 py-3 text-white text-sm placeholder-white/30 focus:border-[#00FFFF]/40 focus:outline-none transition-colors" data-testid="input-phone" />
                    </div>
                    <div className="relative">
                      <MessageSquare size={16} className="absolute left-3 top-3.5 text-white/30" />
                      <textarea placeholder={labels.message} value={form.message} onChange={e => setForm({...form, message: e.target.value})} rows={3}
                        className="w-full bg-[#0f2132] border border-white/10 rounded-xl pl-10 pr-4 py-3 text-white text-sm placeholder-white/30 focus:border-[#00FFFF]/40 focus:outline-none transition-colors resize-none" data-testid="input-message" />
                    </div>
                  </div>
                )}

                {/* Navigation Buttons */}
                <div className="flex justify-between mt-8">
                  {step > 1 ? (
                    <button onClick={() => setStep(step - 1)} className="flex items-center gap-2 px-5 py-3 rounded-xl bg-white/5 text-white/60 hover:bg-white/10 hover:text-white transition-colors text-sm" data-testid="prev-btn">
                      <ArrowLeft size={16} /> {labels.prev}
                    </button>
                  ) : <div />}

                  {step < 3 ? (
                    <button onClick={() => canNext() && setStep(step + 1)} disabled={!canNext()}
                      className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all ${
                        canNext() ? 'bg-[#00FFFF] text-[#030444] hover:bg-[#00FFFF]/80' : 'bg-white/5 text-white/20 cursor-not-allowed'
                      }`} data-testid="next-btn">
                      {labels.next} <ArrowRight size={16} />
                    </button>
                  ) : (
                    <button onClick={handleSubmit} disabled={!canNext() || submitting}
                      className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all ${
                        canNext() && !submitting ? 'bg-[#00FFFF] text-[#030444] hover:bg-[#00FFFF]/80' : 'bg-white/5 text-white/20 cursor-not-allowed'
                      }`} data-testid="submit-btn">
                      {submitting ? <><Loader2 size={16} className="animate-spin" /> {labels.submitting}</> : labels.submit}
                    </button>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      <Footer />
    </div>
  );
}
